import Button from 'components/Button';
import Flex from 'components/Flex';
import { paletteFor } from 'components/GlobalAlertBanner/constants';
import Text from 'components/Text';
import { IGlobalAlert, TAlertType } from 'domain/globalAlerts/types';
import { ReactComponent as CloseIcon } from 'icons/close.svg';
import { isExternalUrl, openExternalUrl } from 'libs/utils';
import React, { FunctionComponent } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

interface IBanner {
  alert: IGlobalAlert;
  onDismiss: (id: number) => void;
  onCtaClick: (id: number) => void;
}

const Banner: FunctionComponent<IBanner> = ({ alert, onDismiss, onCtaClick }) => {
  const navigate = useNavigate();
  const external = Boolean(alert.link_url && isExternalUrl(alert.link_url));
  const hasCta = Boolean(alert.link_url && alert.link_text);

  const handleCtaClick = () => {
    onCtaClick(alert.id);
    if (!alert.link_url) return;
    if (external) {
      openExternalUrl(alert.link_url);
    } else {
      navigate(alert.link_url);
    }
  };

  return (
    <Wrapper
      $variant={alert.alert_type}
      role={alert.alert_type === 'danger' ? 'alert' : 'status'}
      data-test="global-alert"
      data-test-variant={alert.alert_type}
    >
      <Inner alignItems="center" justifyContent="space-between">
        <Message size="sm" color="inherit" lineHeight={24}>
          <Title>{alert.title}</Title> {alert.message}
        </Message>
        <Actions alignItems="center">
          {hasCta && (
            <CtaButton
              styleType="transparent"
              size="sm"
              type="button"
              onClick={handleCtaClick}
              data-test="global-alert-cta"
            >
              {alert.link_text}
            </CtaButton>
          )}
          {alert.dismissible && (
            <Dismiss
              type="button"
              aria-label="Dismiss announcement"
              data-test="global-alert-dismiss"
              onClick={() => onDismiss(alert.id)}
            >
              <CloseIcon />
            </Dismiss>
          )}
        </Actions>
      </Inner>
    </Wrapper>
  );
};

export default Banner;

const Wrapper = styled.div<{ $variant: TAlertType }>`
  width: 100%;
  box-sizing: border-box;
  padding: 0.625rem 1rem;
  background-color: ${({ $variant }) => paletteFor($variant).background};
  color: ${({ $variant }) => paletteFor($variant).foreground};

  ${({ theme }) => theme.media.sm`
    padding: 0.5rem 0.75rem;
  `}
`;

const Inner = styled(Flex)`
  gap: 16px;

  ${({ theme }) => theme.media.sm`
    gap: 10px;
  `}
`;

const Message = styled(Text)`
  flex: 1;
  min-width: 0;
  margin: 0;
  text-align: center;
`;

const Title = styled.span`
  font-weight: 700;
`;

const Actions = styled(Flex)`
  gap: 8px;
  flex-shrink: 0;
`;

const CtaButton = styled(Button)`
  && {
    color: inherit;
    border-color: currentColor;
    padding: 4px 14px;
    line-height: 20px;

    &:hover {
      color: inherit;
      opacity: 0.85;
    }
  }
`;

const Dismiss = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: none;
  background: transparent;
  color: inherit;
  cursor: pointer;
  flex-shrink: 0;

  svg path {
    fill: currentColor;
  }
`;
